// frontend/src/pages/SearchPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, TextField, Button, Paper, Grid, CircularProgress, Alert,
  FormControl, InputLabel, Select, MenuItem, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Avatar, IconButton, Chip, Menu,
  ListItemIcon, Tooltip, TableSortLabel, OutlinedInput, Checkbox, ListItemText,
  FormControlLabel, Autocomplete
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import CancelScheduleSendIcon from '@mui/icons-material/CancelScheduleSend';
import SendIcon from '@mui/icons-material/Send';
import ReplayIcon from '@mui/icons-material/Replay';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';

import { getUserImages, getCommodityValues, archiveBatchImages, unarchiveBatchImages, stageBatchImages, unstageBatchImages, retrySubmitImage, submitBatchImagesToSyndigo, deleteBatchImages, assignCommodityBatch, getUsers } from '../api/images';
import { getScheduledSubmissions } from '../api/scheduler';
import apiClient from '../api/client';
import { useNavigate } from 'react-router-dom';
import commoditiesData from '../data/commodities.json';
import ReportGenerationDialog from '../components/ReportGenerationDialog';
import AssignCommodityDialog from '../components/AssignCommodityDialog';
import DownloadIcon from '@mui/icons-material/Download';
import CategoryIcon from '@mui/icons-material/Category';
import { Divider } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

const logger = console;

// Status display mapping
const STATUS_OPTIONS = [
  { value: 'preview_ready', label: 'Preview Ready', dbStatus: 'success', filters: { is_staged: false, is_submitted: false, is_archived: false } },
  { value: 'staged', label: 'Staged', dbStatus: 'success', filters: { is_staged: true, is_submitted: false, is_archived: false } },
  { value: 'submitted', label: 'Submitted', dbStatus: 'submitted', filters: { is_submitted: true, is_archived: false } },
  { value: 'processing', label: 'Processing', dbStatus: ['processing', 'pending', 'pending_async'] },
  { value: 'failed_generation', label: 'Failed Generation', dbStatus: 'failed_generation' },
  { value: 'failed_submission', label: 'Failed Submission', dbStatus: 'failed_submission' },
  { value: 'archived', label: 'Archived', filters: { is_archived: true } }
];

const ItemActionMenu = ({ image, onEdit, onArchive, onRestore, onStage, onUnstage, onRetrySubmit, onAssignCommodity }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  
  const handleViewDetails = () => {
    navigate(`/images/${image.id}`);
    handleClose();
  };
  
  const handleEditAction = () => {
    onEdit(image.id);
    handleClose();
  };
  
  const handleArchiveAction = () => {
    onArchive([image.id]);
    handleClose();
  };
  
  const handleRestoreAction = () => {
    onRestore([image.id]);
    handleClose();
  };
  
  const handleStageAction = () => {
    onStage([image.id]);
    handleClose();
  };
  
  const handleUnstageAction = () => {
    onUnstage([image.id]);
    handleClose();
  };
  
  const handleRetryAction = () => {
    onRetrySubmit(image.id);
    handleClose();
  };

  const handleAssignCommodityAction = () => {
    onAssignCommodity([image.id]);
    handleClose();
  };

  return (
    <>
      <IconButton aria-label="actions" onClick={handleClick} size="small">
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={handleViewDetails}>
          <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
          View Details
        </MenuItem>
        
        {!image.is_submitted && !image.is_archived && (
          <MenuItem onClick={handleEditAction}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            Edit/Regenerate
          </MenuItem>
        )}
        
        {image.is_submitted && (
          <MenuItem onClick={handleEditAction}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            Edit (New Version)
          </MenuItem>
        )}
        
        {image.status === 'success' && !image.is_archived && !image.is_staged && !image.is_submitted && (
          <MenuItem onClick={handleStageAction}>
            <ListItemIcon><PlaylistAddCheckIcon fontSize="small" /></ListItemIcon>
            Stage for Submission
          </MenuItem>
        )}
        
        {image.is_staged && !image.is_archived && !image.is_submitted && (
          <MenuItem onClick={handleUnstageAction}>
            <ListItemIcon><CancelScheduleSendIcon fontSize="small" /></ListItemIcon>
            Unstage
          </MenuItem>
        )}
        
        {image.status === 'failed_submission' && !image.is_archived && (
          <MenuItem onClick={handleRetryAction}>
            <ListItemIcon><ReplayIcon fontSize="small" /></ListItemIcon>
            Retry Submission
          </MenuItem>
        )}
        
        {image.is_archived && (
          <MenuItem onClick={handleRestoreAction}>
            <ListItemIcon><RestoreFromTrashIcon fontSize="small" /></ListItemIcon>
            Restore to Preview
          </MenuItem>
        )}
        
        <MenuItem onClick={handleAssignCommodityAction}>
          <ListItemIcon><CategoryIcon fontSize="small" /></ListItemIcon>
          Assign Commodity
        </MenuItem>

        {!image.is_archived && !image.is_submitted && (
          <MenuItem onClick={handleArchiveAction} sx={{color: 'warning.dark'}}>
            <ListItemIcon><DeleteForeverIcon fontSize="small" /></ListItemIcon>
            Archive
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

const SearchPage = () => {
  const { user } = useAuth();
  const [images, setImages] = useState([]);
  const [scheduledSubmissions, setScheduledSubmissions] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [processingAction, setProcessingAction] = useState('');
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [commodityDialogOpen, setCommodityDialogOpen] = useState(false);
  const [commodityDialogImageIds, setCommodityDialogImageIds] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [filters, setFilters] = useState({
    commodity: [],
    sub_commodity: [],
    created_after: '',
    created_before: '',
    gtin_search: '',
    status: [],
    user_email: [],
    user_scope: 'current' // 'current' (my images), 'all', or specific user ID
  });
  const [commodityOptions, setCommodityOptions] = useState({ commodities: [], sub_commodities: [] });
  const [userOptions, setUserOptions] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'updated_at', direction: 'desc' });
  const [actionStatus, setActionStatus] = useState(null);
  const navigate = useNavigate();

  // Fetch commodity values and scheduled submissions on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch commodity values
        const values = await getCommodityValues();
        setCommodityOptions(values);
        
        // Fetch scheduled submissions for all users
        const scheduled = await getScheduledSubmissions('pending', true);
        setScheduledSubmissions(scheduled);

        // Fetch users list for the user filter dropdown
        const users = await getUsers();
        setUsersList(users);
      } catch (error) {
        logger.error('Failed to fetch initial data:', error);
      }
    };
    fetchInitialData();
  }, []);

  const searchImages = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSelectedImages([]); // Clear selections on new search

    try {
      // Debug logging for GTIN search
      console.log('GTIN search value:', filters.gtin_search);
      console.log('GTIN search type:', typeof filters.gtin_search);
      console.log('GTIN search length:', filters.gtin_search ? filters.gtin_search.length : 0);

      // Build query params - single efficient query
      const params = {
        limit: 50,  // Keep reasonable limit to avoid memory issues
        skip: 0,
        ...(filters.commodity.length > 0 && { commodity: filters.commodity.join(',') }),
        ...(filters.sub_commodity.length > 0 && { sub_commodity: filters.sub_commodity.join(',') }),
        ...(filters.created_after && { created_after: filters.created_after }),
        ...(filters.created_before && { created_before: filters.created_before }),
        // Only include gtin_search if it has actual content (not just empty string)
        ...(filters.gtin_search && filters.gtin_search.trim() && { gtin_search: filters.gtin_search }),
        // User scope filter - 'current' shows images I've touched, 'all' shows everything
        ...(filters.user_scope === 'current' && user && { touched_by_user_id: user.id }),
        ...(filters.user_scope !== 'current' && filters.user_scope !== 'all' && { user_id_filter: parseInt(filters.user_scope) })
      };

      console.log('Final params being sent to API:', params);

      // Get all images with a single query
      const allImages = await getUserImages(params);
      
      // Filter by status on the frontend if needed
      let filteredImages = allImages;
      if (filters.status.length > 0) {
        filteredImages = allImages.filter(img => {
          // Check each selected status
          return filters.status.some(statusValue => {
            const statusOption = STATUS_OPTIONS.find(opt => opt.value === statusValue);
            if (!statusOption) return false;
            
            // Check if image matches this status option
            if (statusOption.value === 'preview_ready') {
              return img.status === 'success' && !img.is_staged && !img.is_submitted && !img.is_archived;
            } else if (statusOption.value === 'staged') {
              return img.status === 'success' && img.is_staged && !img.is_submitted && !img.is_archived;
            } else if (statusOption.value === 'submitted') {
              return img.is_submitted;
            } else if (statusOption.value === 'failed_generation') {
              return img.status === 'failed_generation' || img.status === 'failed';
            } else if (statusOption.value === 'failed_submission') {
              return img.status === 'failed_submission';
            } else if (statusOption.value === 'archived') {
              return img.is_archived;
            } else if (statusOption.value === 'processing') {
              return ['pending', 'processing', 'pending_async'].includes(img.status);
            }
            return false;
          });
        });
      }
      
      setImages(filteredImages);
      
      // Extract unique user emails from results (include both original and last modified)
      const uniqueEmails = [...new Set(filteredImages
        .flatMap(img => [img.last_modified_by_email, img.user_email])
        .filter(email => email))];
      setUserOptions(uniqueEmails);
      
    } catch (err) {
      logger.error('Error searching images:', err);
      setError(err.message || 'Failed to search images');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Load all images on initial mount
  useEffect(() => {
    searchImages();
  }, []); // Empty dependency array means this runs once on mount

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getImageStatus = (image) => {
    if (image.is_archived) return 'archived';
    if (image.status === 'failed_submission') return 'failed_submission';
    if (image.is_submitted) return 'submitted';
    if (image.status === 'success' && image.is_staged) return 'staged';
    if (image.status === 'success') return 'preview_ready';
    if (['processing', 'pending', 'pending_async'].includes(image.status)) return 'processing';
    if (image.status === 'failed_generation' || image.status === 'failed') return 'failed_generation';
    return 'unknown';
  };

  const sortedImages = useMemo(() => {
    let sortableImages = [...images];
    if (sortConfig.key) {
      sortableImages.sort((a, b) => {
        let aValue, bValue;
        
        if (sortConfig.key === 'status') {
          aValue = getImageStatus(a);
          bValue = getImageStatus(b);
        } else if (sortConfig.key === 'scheduled') {
          const aScheduled = scheduledSubmissions.find(s => s.image_id === a.id && s.status === 'pending');
          const bScheduled = scheduledSubmissions.find(s => s.image_id === b.id && s.status === 'pending');
          aValue = aScheduled ? aScheduled.scheduled_for : null;
          bValue = bScheduled ? bScheduled.scheduled_for : null;
        } else {
          aValue = sortConfig.key.includes('.') 
            ? sortConfig.key.split('.').reduce((obj, key) => obj?.[key], a)
            : a[sortConfig.key];
          bValue = sortConfig.key.includes('.') 
            ? sortConfig.key.split('.').reduce((obj, key) => obj?.[key], b)
            : b[sortConfig.key];
        }
        
        if (aValue === null || aValue === undefined) return sortConfig.direction === 'asc' ? 1 : -1;
        if (bValue === null || bValue === undefined) return sortConfig.direction === 'asc' ? -1 : 1;
        
        if (sortConfig.key.includes('_at') || sortConfig.key.includes('date') || sortConfig.key === 'scheduled') {
          return sortConfig.direction === 'asc' 
            ? new Date(aValue) - new Date(bValue) 
            : new Date(bValue) - new Date(aValue);
        }
        
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableImages;
  }, [images, sortConfig, scheduledSubmissions]);

  const handleSelectImage = (imageId) => {
    setSelectedImages(prev => {
      if (prev.includes(imageId)) {
        return prev.filter(id => id !== imageId);
      } else {
        return [...prev, imageId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedImages.length === sortedImages.length) {
      setSelectedImages([]);
    } else {
      setSelectedImages(sortedImages.map(img => img.id));
    }
  };

  const handleEditImage = (imageId) => {
    navigate(`/images/${imageId}/edit`);
  };

  const handleArchiveImages = async (imageIds) => {
    setProcessingAction('archive');
    try {
      await archiveBatchImages({ image_ids: imageIds });
      setActionStatus({ type: 'success', message: `${imageIds.length} image(s) archived successfully` });
      setSelectedImages([]);
      searchImages();
    } catch (err) {
      setActionStatus({ type: 'error', message: err.message || 'Failed to archive images' });
    } finally {
      setProcessingAction('');
    }
  };

  const handleRestoreImages = async (imageIds) => {
    setProcessingAction('restore');
    try {
      await unarchiveBatchImages({ image_ids: imageIds });
      setActionStatus({ type: 'success', message: `${imageIds.length} image(s) restored successfully` });
      setSelectedImages([]);
      searchImages();
    } catch (err) {
      setActionStatus({ type: 'error', message: err.message || 'Failed to restore images' });
    } finally {
      setProcessingAction('');
    }
  };

  const handleStageImages = async (imageIds) => {
    setProcessingAction('stage');
    try {
      await stageBatchImages({ image_ids: imageIds });
      setActionStatus({ type: 'success', message: `${imageIds.length} image(s) staged successfully` });
      setSelectedImages([]);
      searchImages();
    } catch (err) {
      setActionStatus({ type: 'error', message: err.message || 'Failed to stage images' });
    } finally {
      setProcessingAction('');
    }
  };

  const handleUnstageImages = async (imageIds) => {
    setProcessingAction('unstage');
    try {
      await unstageBatchImages({ image_ids: imageIds });
      setActionStatus({ type: 'success', message: `${imageIds.length} image(s) unstaged successfully` });
      setSelectedImages([]);
      searchImages();
    } catch (err) {
      setActionStatus({ type: 'error', message: err.message || 'Failed to unstage images' });
    } finally {
      setProcessingAction('');
    }
  };

  const handleRetrySubmit = async (imageId) => {
    try {
      await retrySubmitImage(imageId);
      setActionStatus({ type: 'success', message: 'Retry submission initiated' });
      searchImages();
    } catch (err) {
      setActionStatus({ type: 'error', message: err.message || 'Failed to retry submission' });
    }
  };

  const handleSubmitImages = async (imageIds) => {
    setProcessingAction('submit');
    try {
      await submitBatchImagesToSyndigo({ image_ids: imageIds });
      setActionStatus({ type: 'success', message: `${imageIds.length} image(s) submitted successfully` });
      setSelectedImages([]);
      searchImages();
    } catch (err) {
      setActionStatus({ type: 'error', message: err.message || 'Failed to submit images' });
    } finally {
      setProcessingAction('');
    }
  };

  const handleDeleteImages = async (imageIds) => {
    if (!window.confirm(`Are you sure you want to permanently delete ${imageIds.length} image(s)?`)) {
      return;
    }
    setProcessingAction('delete');
    try {
      await deleteBatchImages({ image_ids: imageIds });
      setActionStatus({ type: 'success', message: `${imageIds.length} image(s) deleted permanently` });
      setSelectedImages([]);
      searchImages();
    } catch (err) {
      setActionStatus({ type: 'error', message: err.message || 'Failed to delete images' });
    } finally {
      setProcessingAction('');
    }
  };

  const openCommodityDialog = (imageIds) => {
    setCommodityDialogImageIds(imageIds);
    setCommodityDialogOpen(true);
  };

  const handleAssignCommodity = async (commodity, subCommodity) => {
    setProcessingAction('assign_commodity');
    try {
      const payload = { image_ids: commodityDialogImageIds };
      if (commodity !== '') payload.commodity = commodity;
      if (subCommodity !== '') payload.sub_commodity = subCommodity;
      await assignCommodityBatch(payload);
      setActionStatus({ type: 'success', message: `Commodity assigned to ${commodityDialogImageIds.length} image(s)` });
      setSelectedImages([]);
      setCommodityDialogOpen(false);
      searchImages();
    } catch (err) {
      setActionStatus({ type: 'error', message: err.message || 'Failed to assign commodity' });
    } finally {
      setProcessingAction('');
    }
  };

  const getStatusChip = (image) => {
    if (image.is_archived) return <Chip label="Archived" variant="outlined" size="small" icon={<DeleteForeverIcon />} />;
    if (image.status === 'failed_submission') return <Chip label="Submission Failed" color="error" variant="outlined" size="small" icon={<ReportProblemIcon />} />;
    if (image.is_submitted) return <Chip label="Submitted" color="primary" size="small" icon={<CheckCircleIcon />} />;
    if (image.status === 'success' && image.is_staged) {
      // Check if it's scheduled
      const scheduled = scheduledSubmissions.find(s => s.image_id === image.id && s.status === 'pending');
      if (scheduled) {
        return <Chip label="Scheduled" color="info" size="small" icon={<ScheduleIcon />} />;
      }
      return <Chip label="Staged" color="secondary" size="small" icon={<PlaylistAddCheckIcon />} />;
    }
    if (image.status === 'success') return <Chip label="Preview Ready" color="success" size="small" icon={<CheckCircleOutlineIcon />} />;
    if (['processing', 'pending', 'pending_async'].includes(image.status)) return <Chip label="Processing" color="warning" size="small" icon={<HourglassEmptyIcon />} />;
    if (image.status === 'failed_generation' || image.status === 'failed') return <Chip label="Generation Failed" color="error" size="small" icon={<ErrorIcon />} />;
    return <Chip label={image.status || "Unknown"} size="small" />;
  };

  const getScheduledDate = (imageId) => {
    const scheduled = scheduledSubmissions.find(s => s.image_id === imageId && s.status === 'pending');
    return scheduled ? new Date(scheduled.scheduled_for) : null;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Report & Search</Typography>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={() => setReportDialogOpen(true)}
        >
          Generate Report
        </Button>
      </Box>
      
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="GTIN Search"
              placeholder="Enter GTIN(s) - comma separated or paste a list"
              value={filters.gtin_search || ''}
              multiline
              minRows={1}
              maxRows={3}
              onChange={(e) => {
                setFilters(prev => ({
                  ...prev,
                  gtin_search: e.target.value
                }));
              }}
              onPaste={(e) => {
                e.preventDefault();
                const pasted = e.clipboardData.getData('text');
                const cleaned = pasted
                  .split(/[\n\r,\t]+/)
                  .map(s => s.trim())
                  .filter(Boolean)
                  .join(', ');
                setFilters(prev => {
                  const existing = prev.gtin_search ? prev.gtin_search.trim() : '';
                  const separator = existing && !existing.endsWith(',') ? ', ' : '';
                  return { ...prev, gtin_search: existing + separator + cleaned };
                });
              }}
              helperText="Enter or paste GTINs (comma, newline, or tab separated)"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              multiple
              size="small"
              options={commoditiesData}
              getOptionLabel={(option) => option.label}
              value={commoditiesData.filter(c => filters.commodity.includes(c.value))}
              onChange={(event, newValue) => {
                setFilters(prev => ({ 
                  ...prev, 
                  commodity: newValue.map(item => item.value) 
                }));
              }}
              filterOptions={(options, { inputValue }) => {
                const input = inputValue.toLowerCase();
                return options.filter(option => 
                  option.value.toLowerCase().includes(input) || 
                  option.description.toLowerCase().includes(input)
                );
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <strong>{option.value}</strong> - {option.description}
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Commodity"
                  placeholder="Type to search..."
                />
              )}
              fullWidth
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              multiple
              size="small"
              options={commodityOptions.sub_commodities || []}
              value={filters.sub_commodity || []}
              onChange={(event, newValue) => {
                setFilters(prev => ({ 
                  ...prev, 
                  sub_commodity: newValue
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sub-commodity"
                  placeholder="Type to search..."
                />
              )}
              fullWidth
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                multiple
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                input={<OutlinedInput label="Status" />}
                renderValue={(selected) => selected.map(val => STATUS_OPTIONS.find(opt => opt.value === val)?.label || val).join(', ')}
              >
                {STATUS_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox checked={filters.status.indexOf(option.value) > -1} />
                    <ListItemText primary={option.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Created After"
              value={filters.created_after}
              onChange={(e) => setFilters(prev => ({ ...prev, created_after: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Created Before"
              value={filters.created_before}
              onChange={(e) => setFilters(prev => ({ ...prev, created_before: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>User</InputLabel>
              <Select
                value={filters.user_scope}
                label="User"
                onChange={(e) => setFilters(prev => ({ ...prev, user_scope: e.target.value }))}
              >
                <MenuItem value="current">My Images</MenuItem>
                <MenuItem value="all">All Users</MenuItem>
                {usersList.length > 0 && <Divider />}
                {usersList.map(u => (
                  <MenuItem key={u.id} value={u.id.toString()}>
                    {u.username} ({u.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={searchImages}
                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                disabled={loading}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={() => setFilters({
                  commodity: [],
                  sub_commodity: [],
                  created_after: '',
                  created_before: '',
                  gtin_search: '',
                  status: [],
                  user_email: [],
                  user_scope: 'current'
                })}
              >
                Clear
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {actionStatus && (
        <Alert severity={actionStatus.type} sx={{ mb: 2 }} onClose={() => setActionStatus(null)}>
          {actionStatus.message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && sortedImages.length > 0 && (
        <>
          <Paper elevation={1} sx={{ mb: 1.5, p: 1, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', borderRadius: 2, gap: 1 }}>
            <FormControlLabel 
              control={
                <Checkbox 
                  checked={selectedImages.length === sortedImages.length && sortedImages.length > 0} 
                  onChange={handleSelectAll} 
                  disabled={!!processingAction || sortedImages.length === 0} 
                />
              } 
              label={`Select All (${selectedImages.length}/${sortedImages.length})`} 
            />
            
            {selectedImages.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {/* Show stage button if any selected images can be staged */}
                {selectedImages.some(id => {
                  const img = sortedImages.find(i => i.id === id);
                  return img && img.status === 'success' && !img.is_archived && !img.is_staged && !img.is_submitted;
                }) && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={processingAction === 'stage' ? <CircularProgress size={18} color="inherit"/> : <PlaylistAddCheckIcon />}
                    onClick={() => handleStageImages(selectedImages)}
                    disabled={!!processingAction}
                  >
                    Stage ({selectedImages.length})
                  </Button>
                )}
                
                {/* Show unstage button if any selected images can be unstaged */}
                {selectedImages.some(id => {
                  const img = sortedImages.find(i => i.id === id);
                  return img && img.is_staged && !img.is_archived && !img.is_submitted;
                }) && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={processingAction === 'unstage' ? <CircularProgress size={18} color="inherit"/> : <CancelScheduleSendIcon />}
                    onClick={() => handleUnstageImages(selectedImages)}
                    disabled={!!processingAction}
                  >
                    Unstage ({selectedImages.length})
                  </Button>
                )}
                
                {/* Show submit button if any selected images are staged */}
                {selectedImages.some(id => {
                  const img = sortedImages.find(i => i.id === id);
                  return img && img.is_staged && !img.is_archived && !img.is_submitted;
                }) && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={processingAction === 'submit' ? <CircularProgress size={18} color="inherit"/> : <SendIcon />}
                    onClick={() => handleSubmitImages(selectedImages)}
                    disabled={!!processingAction}
                  >
                    Submit ({selectedImages.length})
                  </Button>
                )}
                
                {/* Archive button - for non-archived, non-submitted images */}
                {selectedImages.some(id => {
                  const img = sortedImages.find(i => i.id === id);
                  return img && !img.is_archived && !img.is_submitted;
                }) && (
                  <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    startIcon={processingAction === 'archive' ? <CircularProgress size={18} color="inherit"/> : <DeleteForeverIcon />}
                    onClick={() => handleArchiveImages(selectedImages)}
                    disabled={!!processingAction}
                  >
                    Archive ({selectedImages.length})
                  </Button>
                )}
                
                {/* Restore button - for archived images */}
                {selectedImages.some(id => {
                  const img = sortedImages.find(i => i.id === id);
                  return img && img.is_archived;
                }) && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={processingAction === 'restore' ? <CircularProgress size={18} color="inherit"/> : <RestoreFromTrashIcon />}
                    onClick={() => handleRestoreImages(selectedImages)}
                    disabled={!!processingAction}
                  >
                    Restore ({selectedImages.length})
                  </Button>
                )}
                
                {/* Delete button - for non-submitted images only */}
                {selectedImages.some(id => {
                  const img = sortedImages.find(i => i.id === id);
                  return img && !img.is_submitted;
                }) && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={processingAction === 'delete' ? <CircularProgress size={18} color="inherit"/> : <DeleteForeverIcon />}
                    onClick={() => handleDeleteImages(selectedImages)}
                    disabled={!!processingAction}
                  >
                    Delete ({selectedImages.length})
                  </Button>
                )}

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={processingAction === 'assign_commodity' ? <CircularProgress size={18} color="inherit"/> : <CategoryIcon />}
                  onClick={() => openCommodityDialog(selectedImages)}
                  disabled={!!processingAction}
                >
                  Assign Commodity ({selectedImages.length})
                </Button>
              </Box>
            )}
          </Paper>
          
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ minWidth: 50 }}>
                  <Checkbox
                    checked={selectedImages.length === sortedImages.length && sortedImages.length > 0}
                    onChange={handleSelectAll}
                    disabled={!!processingAction}
                  />
                </TableCell>
                <TableCell sx={{ minWidth: 60 }}></TableCell>
                <TableCell sx={{ minWidth: 120 }}>
                  <TableSortLabel
                    active={sortConfig.key === 'gtin'}
                    direction={sortConfig.key === 'gtin' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('gtin')}
                  >
                    GTIN
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ minWidth: 120 }}>
                  <TableSortLabel
                    active={sortConfig.key === 'last_modified_by_email'}
                    direction={sortConfig.key === 'last_modified_by_email' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('last_modified_by_email')}
                  >
                    Last Created By
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ minWidth: 180 }}>
                  <TableSortLabel
                    active={sortConfig.key === 'product_description'}
                    direction={sortConfig.key === 'product_description' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('product_description')}
                  >
                    Description
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ minWidth: 120 }}>
                  <TableSortLabel
                    active={sortConfig.key === 'commodity'}
                    direction={sortConfig.key === 'commodity' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('commodity')}
                  >
                    Commodity
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ minWidth: 100 }}>
                  <TableSortLabel
                    active={sortConfig.key === 'status'}
                    direction={sortConfig.key === 'status' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ minWidth: 100 }}>
                  <TableSortLabel
                    active={sortConfig.key === 'created_at'}
                    direction={sortConfig.key === 'created_at' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('created_at')}
                  >
                    Created
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ minWidth: 110 }}>
                  <TableSortLabel
                    active={sortConfig.key === 'updated_at'}
                    direction={sortConfig.key === 'updated_at' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('updated_at')}
                  >
                    Last Updated
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ minWidth: 100 }}>
                  <TableSortLabel
                    active={sortConfig.key === 'scheduled'}
                    direction={sortConfig.key === 'scheduled' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('scheduled')}
                  >
                    Scheduled
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ minWidth: 70 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedImages.map((image) => {
                const scheduledDate = getScheduledDate(image.id);
                return (
                  <TableRow key={image.id} hover selected={selectedImages.includes(image.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedImages.includes(image.id)}
                        onChange={() => handleSelectImage(image.id)}
                        disabled={!!processingAction}
                      />
                    </TableCell>
                    <TableCell>
                      <Avatar
                        variant="rounded"
                        src={(image.status === 'success' || image.is_submitted || image.is_staged) && image.file_path
                          ? `${apiClient.defaults.baseURL}/images/${image.id}/thumbnail?size=100&t=${new Date().getTime()}`
                          : undefined
                        }
                        sx={{ width: 48, height: 48, cursor: 'pointer' }}
                        onClick={() => navigate(`/images/${image.id}`)}
                      >
                        {!image.file_path && 'N/A'}
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>{image.gtin}</Typography>
                      {image.original_gtin && image.original_gtin !== image.gtin && (
                        <Tooltip title={`Original: ${image.original_gtin}`}>
                          <Typography variant="caption" color="textSecondary">
                            <InfoIcon fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                            Normalized
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>{image.last_modified_by_email || image.user_email || 'Unknown'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          lineHeight: 1.2
                        }}
                        title={image.product_description}
                      >
                        {image.product_description || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {image.commodity || ''}
                        {image.commodity && image.sub_commodity ? ' / ' : ''}
                        {image.sub_commodity || ''}
                      </Typography>
                    </TableCell>
                    <TableCell>{getStatusChip(image)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(image.created_at).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(image.updated_at).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {scheduledDate && (
                        <Tooltip title={scheduledDate.toLocaleString()}>
                          <Typography variant="body2">
                            {scheduledDate.toLocaleDateString()}
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <ItemActionMenu
                        image={image}
                        onEdit={handleEditImage}
                        onArchive={handleArchiveImages}
                        onRestore={handleRestoreImages}
                        onStage={handleStageImages}
                        onUnstage={handleUnstageImages}
                        onRetrySubmit={handleRetrySubmit}
                        onAssignCommodity={openCommodityDialog}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        </>
      )}

      {!loading && sortedImages.length === 0 && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            No images found. Try adjusting your search criteria.
          </Typography>
        </Paper>
      )}
      
      <AssignCommodityDialog
        open={commodityDialogOpen}
        onClose={() => setCommodityDialogOpen(false)}
        selectedCount={commodityDialogImageIds.length}
        onAssign={handleAssignCommodity}
        isAssigning={processingAction === 'assign_commodity'}
      />

      <ReportGenerationDialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        filters={{
          status: filters.status.length > 0 ? filters.status.join(',') : null,
          gtin_search: filters.gtin_search || null,
          commodity: filters.commodity.length > 0 ? filters.commodity.join(',') : null,
          sub_commodity: filters.sub_commodity.length > 0 ? filters.sub_commodity.join(',') : null,
          created_after: filters.created_after || null,
          created_before: filters.created_before || null,
          ...(filters.user_scope === 'current' && user && { touched_by_user_id: user.id }),
          ...(filters.user_scope !== 'current' && filters.user_scope !== 'all' && { user_id_filter: parseInt(filters.user_scope) })
        }}
        onGenerate={() => {
          // Optional: Show success message
          setActionStatus({ type: 'success', message: 'Report generated successfully!' });
        }}
      />
    </Box>
  );
};

export default SearchPage;